'use client'

import { useState, useEffect, useCallback } from 'react'
import { useLocale } from 'next-intl'
import { Link, useRouter } from '@/i18n/navigation'
import { useTheme } from 'next-themes'
import FinancialChart, { type ChartType, type OhlcvDataPoint, type OhlcvCrosshair } from '@/components/FinancialChart'
import { fetchForexRates, getRate, fetchForexChartData } from '@/services/forex'
import InstrumentAbout from '@/components/InstrumentAbout'

interface Props {
  pair: string
}

const periods = [
  { key: '1G', tr: '1G', en: '1D' },
  { key: '1H', tr: '1H', en: '1W' },
  { key: '1A', tr: '1A', en: '1M' },
  { key: '3A', tr: '3A', en: '3M' },
  { key: '1Y', tr: '1Y', en: '1Y' },
  { key: '5Y', tr: '5Y', en: '5Y' },
]

const pairNames: Record<string, Record<string, string>> = {
  'USDTRY': { tr: 'Amerikan Doları / Türk Lirası', en: 'US Dollar / Turkish Lira' },
  'EURTRY': { tr: 'Euro / Türk Lirası', en: 'Euro / Turkish Lira' },
  'EURUSD': { tr: 'Euro / Amerikan Doları', en: 'Euro / US Dollar' },
  'GBPTRY': { tr: 'İngiliz Sterlini / Türk Lirası', en: 'British Pound / Turkish Lira' },
  'GBPUSD': { tr: 'İngiliz Sterlini / Amerikan Doları', en: 'British Pound / US Dollar' },
  'JPYTRY': { tr: 'Japon Yeni / Türk Lirası', en: 'Japanese Yen / Turkish Lira' },
}

function parsePair(raw: string): { base: string; quote: string; slash: string; key: string } {
  const upper = raw.toUpperCase()
  if (upper.includes('/')) {
    const [base, quote] = upper.split('/')
    return { base, quote, slash: `${base}/${quote}`, key: `${base}${quote}` }
  }
  if (upper.includes('-')) {
    const [base, quote] = upper.split('-')
    return { base, quote, slash: `${base}/${quote}`, key: `${base}${quote}` }
  }
  if (upper.length === 6) {
    const base = upper.slice(0, 3)
    const quote = upper.slice(3)
    return { base, quote, slash: `${base}/${quote}`, key: upper }
  }
  return { base: upper, quote: '', slash: upper, key: upper }
}

function fmtVal(val: number): string {
  if (val === 0) return '\u2014'
  const [int, dec] = val.toFixed(4).split('.')
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + dec
}

function Skeleton({ w, h = 16, className = '' }: { w: number; h?: number; className?: string }) {
  return (
    <div
      className={`animate-pulse rounded ${className}`}
      style={{ width: w, height: h, backgroundColor: 'var(--border-color)' }}
    />
  )
}

function IconCandle({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="2" x2="8" y2="6" /><rect x="5.5" y="6" width="5" height="10" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="8" y1="16" x2="8" y2="22" />
      <line x1="17" y1="4" x2="17" y2="8" /><rect x="14.5" y="8" width="5" height="8" rx="1" fill={active ? 'var(--accent-color)' : 'none'} /><line x1="17" y1="16" x2="17" y2="20" />
    </svg>
  )
}

function IconLine({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2 18 8 12 14 16 22 6" />
    </svg>
  )
}

function IconArea({ active }: { active: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20 L8 12 L14 16 L22 6 L22 20 Z" fill={active ? 'rgba(52,152,219,0.2)' : 'rgba(128,128,128,0.1)'} />
      <polyline points="2 20 8 12 14 16 22 6" stroke={active ? 'var(--accent-color)' : 'var(--text-muted)'} strokeWidth="2" fill="none" />
    </svg>
  )
}

export default function ForexDetailClient({ pair: pairParam }: Props) {
  const lang = useLocale()
  const router = useRouter()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const parsed = parsePair(pairParam || '')
  const { base, quote: quoteCur, slash: pairSlash, key: pairKey } = parsed

  const [price, setPrice] = useState(0)
  const [prevClose, setPrevClose] = useState(0)
  const [ohlcvData, setOhlcvData] = useState<OhlcvDataPoint[]>([])
  const [period, setPeriod] = useState('1A')
  const [chartType, setChartType] = useState<ChartType>('candlestick')
  const [crosshair, setCrosshair] = useState<OhlcvCrosshair | null>(null)
  const [chartLoading, setChartLoading] = useState(true)
  const [chartUpdatedAt, setChartUpdatedAt] = useState<number>(0)

  const displayName = pairNames[pairKey]?.[lang] || pairNames[pairKey]?.['en'] || pairSlash

  const loadPrice = useCallback(async () => {
    if (!base || !quoteCur) return
    const rates = await fetchForexRates()
    const p = getRate(rates, base, quoteCur)
    if (p > 0) setPrice(p)
  }, [base, quoteCur])

  useEffect(() => {
    loadPrice()
    const iv = setInterval(loadPrice, 60_000)
    return () => clearInterval(iv)
  }, [loadPrice])

  useEffect(() => {
    if (!pairSlash) return
    setChartLoading(true)
    setCrosshair(null)
    fetchForexChartData(pairSlash, period).then(result => {
      const points: OhlcvDataPoint[] = (result?.data ?? []).map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
        volume: d.volume,
      }))
      setOhlcvData(points)
      if (points.length >= 2) setPrevClose(points[points.length - 2].close)
      setChartUpdatedAt(Date.now())
      setChartLoading(false)
    }).catch(() => { setOhlcvData([]); setChartLoading(false) })
  }, [pairSlash, period])

  if (!base || !quoteCur) {
    return (
      <main className="max-w-7xl mx-auto py-5 px-4">
        <div className="text-center py-24">
          <h1 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            {lang === 'en' ? 'Currency pair not found' : 'Döviz çifti bulunamadı'}
          </h1>
          <button
            onClick={() => router.push('/')}
            className="px-6 py-2.5 rounded-lg font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent-color)', color: '#fff' }}
          >
            {lang === 'en' ? 'Back to Home' : 'Ana Sayfaya Dön'}
          </button>
        </div>
      </main>
    )
  }

  const change = prevClose > 0 ? price - prevClose : 0
  const changePct = prevClose > 0 ? (change / prevClose) * 100 : 0
  const isUp = change >= 0
  const clr = isUp ? 'var(--chart-up)' : 'var(--chart-down)'

  const lastOhlcv = ohlcvData.length > 0 ? ohlcvData[ohlcvData.length - 1] : null
  const ohlcDisplay = crosshair || lastOhlcv

  return (
    <main className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-[12px] mb-4 overflow-hidden whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
        <Link href="/" className="hover:underline transition-colors" style={{ color: 'var(--accent-color)' }}>
          {lang === 'en' ? 'Home' : 'Anasayfa'}
        </Link>
        <span className="select-none">/</span>
        <span>{lang === 'en' ? 'Forex' : 'Döviz'}</span>
        <span className="select-none">/</span>
        <span className="font-semibold" style={{ color: 'var(--text-main)' }}>{pairSlash}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight m-0 flex items-baseline gap-2" style={{ color: 'var(--text-main)' }}>
            {pairSlash}
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>({displayName})</span>
          </h1>
        </div>
        {price > 0 ? (
          <div className="flex items-baseline gap-3 shrink-0">
            <span className="text-[28px] font-bold tabular-nums" style={{ color: clr }}>
              {fmtVal(price)}
            </span>
            {prevClose > 0 && (
              <span className="text-[15px] font-semibold tabular-nums" style={{ color: clr }}>
                {isUp ? '+' : ''}{fmtVal(change)}{' '}
                ({isUp ? '+' : ''}%{Math.abs(changePct).toFixed(2).replace('.', ',')})
                <svg className="inline-block ml-1" width="10" height="10" viewBox="0 0 10 10" fill={clr}>
                  {isUp ? <polygon points="5,1 9,7 1,7" /> : <polygon points="5,9 9,3 1,3" />}
                </svg>
              </span>
            )}
          </div>
        ) : (
          <div className="flex items-baseline gap-3">
            <Skeleton w={140} h={28} />
            <Skeleton w={100} h={18} />
          </div>
        )}
      </div>

      {/* Chart Card */}
      <div
        className="rounded-xl border overflow-hidden mb-5"
        style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
      >
        {/* OHLC Row */}
        {ohlcDisplay && (
          <div
            className="flex items-center gap-4 sm:gap-6 px-4 sm:px-5 pt-3 pb-2 flex-wrap"
            style={{ borderBottom: '1px solid var(--border-color)' }}
          >
            {[
              { label: 'O', value: ohlcDisplay.open },
              { label: 'H', value: ohlcDisplay.high, color: 'var(--chart-up)' },
              { label: 'L', value: ohlcDisplay.low, color: 'var(--chart-down)' },
              { label: 'C', value: ohlcDisplay.close },
            ].map(item => (
              <div key={item.label} className="flex items-baseline gap-1.5">
                <span className="text-[10px] font-bold uppercase" style={{ color: 'var(--text-muted)' }}>{item.label}</span>
                <span className="text-[13px] font-semibold tabular-nums" style={{ color: item.color || 'var(--text-main)' }}>
                  {fmtVal(item.value)}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div
          className="flex items-center justify-between px-4 sm:px-5 py-2"
          style={{ borderBottom: '1px solid var(--border-color)' }}
        >
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {periods.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className="px-3 py-1 rounded-md text-[12px] font-semibold transition-colors whitespace-nowrap"
                style={{
                  backgroundColor: period === p.key ? 'var(--accent-color)' : 'transparent',
                  color: period === p.key ? '#fff' : 'var(--text-muted)',
                }}
              >
                {lang === 'en' ? p.en : p.tr}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0.5 ml-3 shrink-0">
            <div className="h-4 w-px mx-2" style={{ backgroundColor: 'var(--border-color)' }} />
            {([
              { type: 'candlestick' as ChartType, Icon: IconCandle },
              { type: 'line' as ChartType, Icon: IconLine },
              { type: 'area' as ChartType, Icon: IconArea },
            ]).map(({ type, Icon }) => (
              <button
                key={type}
                onClick={() => setChartType(type)}
                className="p-1.5 rounded transition-colors"
                style={{
                  backgroundColor: chartType === type ? (isDark ? 'rgba(52,152,219,0.15)' : 'rgba(52,152,219,0.1)') : 'transparent',
                }}
                title={type}
              >
                <Icon active={chartType === type} />
              </button>
            ))}
          </div>
        </div>

        {/* Chart */}
        <div className="px-0">
          {chartLoading ? (
            <div className="animate-pulse" style={{ height: 480, backgroundColor: 'var(--border-color)', opacity: 0.15 }} />
          ) : ohlcvData.length === 0 ? (
            <div className="flex items-center justify-center" style={{ height: 480 }}>
              <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                {lang === 'en' ? 'No chart data available' : 'Grafik verisi bulunamadı'}
              </span>
            </div>
          ) : (
            <FinancialChart
              data={ohlcvData}
              isDark={isDark}
              height={480}
              chartType={chartType}
              showVolume={false}
              onCrosshairMove={setCrosshair}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-5 py-2 text-[11px]" style={{ color: 'var(--text-muted)', borderTop: '1px solid var(--border-color)' }}>
          {lang === 'en' ? 'Last updated' : 'Son güncelleme'}: {chartUpdatedAt > 0
            ? (() => {
                const tz = 'Europe/Istanbul'
                const d = new Date(chartUpdatedAt)
                return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', { timeZone: tz }) + ' ' + d.toLocaleTimeString(lang === 'en' ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit', timeZone: tz })
              })()
            : '\u2014'}
        </div>
      </div>

      {/* Info Bar */}
      {lastOhlcv && price > 0 && (
        <div
          className="rounded-xl border overflow-hidden mb-5 px-5 py-4"
          style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
            {[
              { label: lang === 'en' ? 'Close' : 'Kapanış', value: fmtVal(lastOhlcv.close), color: 'var(--text-main)' },
              { label: lang === 'en' ? 'Open' : 'Açılış', value: fmtVal(lastOhlcv.open), color: 'var(--text-muted)' },
              { label: lang === 'en' ? 'Low' : 'En Düşük', value: fmtVal(lastOhlcv.low), color: 'var(--chart-down)' },
              { label: lang === 'en' ? 'High' : 'En Yüksek', value: fmtVal(lastOhlcv.high), color: 'var(--chart-up)' },
            ].map(s => (
              <div key={s.label}>
                <div className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
                <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <InstrumentAbout symbol={pairSlash} kind="forex" />
    </main>
  )
}
